import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import SyncLockRoundedIcon from '@mui/icons-material/SyncLockRounded';
import { getApiUrl } from '../utils/api';
import { apiFetch } from '../utils/apiFetch';

const API_URL = getApiUrl();

const WaiterDeviceConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rotateDialogOpen, setRotateDialogOpen] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    let active = true;

    const loadConfig = async () => {
      try {
        const response = await apiFetch(`${API_URL}/api/salon/device/config`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === false || !data.pin) {
          throw new Error(data.error || data.message || 'Falha ao carregar o acesso do Garçom.');
        }
        if (active) {
          setConfig(data);
          setError('');
        }
      } catch (err) {
        if (active) setError(err.message || 'Falha ao carregar o acesso do Garçom.');
      } finally {
        if (active) setLoading(false);
      }
    };

    loadConfig();
    return () => {
      active = false;
    };
  }, []);

  const accessLink = typeof window === 'undefined'
    ? '/garcom/acesso'
    : `${window.location.origin}${config?.accessPath || '/garcom/acesso'}`;
  const pinDigits = String(config?.pin || '').slice(0, 6).split('');
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copyValue = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(`${label} copiado.`);
    } catch {
      setFeedback(`Não foi possível copiar ${label.toLowerCase()}.`);
    }
  };

  const shareAccess = async () => {
    try {
      await navigator.share({
        title: 'Acesso do Garçom',
        text: `Abra o painel Garçom e use o PIN ${config.pin}.`,
        url: accessLink,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') setFeedback('Não foi possível compartilhar o acesso.');
    }
  };

  const rotatePin = async () => {
    setRotating(true);
    try {
      const response = await apiFetch(`${API_URL}/api/salon/device/config/rotate`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false || !data.pin) {
        throw new Error(data.error || data.message || 'Falha ao gerar um novo PIN.');
      }
      setConfig(data);
      setError('');
      setRotateDialogOpen(false);
      setFeedback('Novo PIN gerado. Os aparelhos anteriores foram desconectados.');
    } catch (err) {
      setError(err.message || 'Falha ao gerar um novo PIN.');
      setRotateDialogOpen(false);
    } finally {
      setRotating(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2, border: '1px solid #bbf7d0', borderRadius: 3, boxShadow: '0 10px 30px rgba(15,23,42,0.06)' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <KeyRoundedIcon sx={{ color: '#15803d' }} />
          <Typography variant="h6">Conectar celular ou tablet do garçom</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Envie o link universal para o aparelho e informe o PIN abaixo. O acesso ficará vinculado somente a este estabelecimento.
        </Typography>

        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">Carregando acesso do Garçom…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={2} alignItems="stretch">
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Link de acesso</Typography>
              <TextField
                fullWidth
                value={accessLink}
                slotProps={{ input: { readOnly: true } }}
                inputProps={{ 'aria-label': 'Link público de acesso do Garçom' }}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={() => copyValue(accessLink, 'Link')}>
                  Copiar link
                </Button>
                {canShare ? (
                  <Button variant="outlined" startIcon={<ShareRoundedIcon />} onClick={shareAccess}>
                    Compartilhar
                  </Button>
                ) : null}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.75 }}>PIN deste estabelecimento</Typography>
              <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} aria-label={`PIN ${config.pin}`}>
                {pinDigits.map((digit, index) => (
                  <Box
                    key={`${digit}-${index}`}
                    aria-hidden="true"
                    sx={{
                      width: { xs: 36, sm: 50 },
                      height: 58,
                      display: 'grid',
                      placeItems: 'center',
                      borderRadius: 2,
                      border: '2px solid #bbf7d0',
                      background: '#f0fdf4',
                      color: '#14532d',
                      fontSize: '1.65rem',
                      fontWeight: 900,
                    }}
                  >
                    {digit}
                  </Box>
                ))}
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5 }}>
                <Button variant="outlined" startIcon={<ContentCopyRoundedIcon />} onClick={() => copyValue(String(config.pin), 'PIN')}>
                  Copiar PIN
                </Button>
                <Button variant="text" color="warning" startIcon={<SyncLockRoundedIcon />} onClick={() => setRotateDialogOpen(true)}>
                  Gerar novo PIN
                </Button>
              </Stack>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Dialog open={rotateDialogOpen} onClose={() => !rotating && setRotateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Gerar novo PIN do Garçom?</DialogTitle>
        <DialogContent>
          <Typography>
            Os celulares e tablets conectados com o PIN atual serão desconectados e precisarão informar o novo código.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateDialogOpen(false)} disabled={rotating}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={rotatePin} disabled={rotating}>
            {rotating ? 'Gerando…' : 'Gerar novo PIN'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={3500}
        onClose={() => setFeedback('')}
        message={feedback}
      />
    </>
  );
};

export default WaiterDeviceConfig;
